import { Injectable } from '@angular/core';
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  isSameDay,
  isWithinInterval,
  startOfWeek,
  isToday,
  endOfWeek,
  addDays,
} from 'date-fns';

@Injectable()
export class DateRangeService {
  // Returns the days of the month with padding for correct calendar display
  getDaysInMonthWithPadding(date: Date): (Date | null)[] {
    const startOfMonthDate = startOfMonth(date);
    const endOfMonthDate = endOfMonth(date);

    // Determine the start of the calendar (the start of the week containing the start of the month)
    const startOfCalendar = startOfWeek(startOfMonthDate, { weekStartsOn: 0 });

    // Determine the end of the calendar (the end of the week containing the end of the month)
    const endOfCalendar = endOfWeek(endOfMonthDate, { weekStartsOn: 0 });

    const days: (Date | null)[] = [];

    // Loop through the calendar range and populate the days array
    for (
      let current = startOfCalendar;
      current <= endOfCalendar;
      current = addDays(current, 1)
    ) {
      if (current < startOfMonthDate || current > endOfMonthDate) {
        days.push(null); // Padding days outside the month
      } else {
        days.push(new Date(current)); // Valid days within the month
      }
    }
    return days;
  }

  getAdjacentMonths(currentMonth: Date): {
    firstMonthStart: Date;
    secondMonthStart: Date;
  } {
    const firstMonthStart = startOfMonth(currentMonth);
    const secondMonthStart = addMonths(firstMonthStart, 1);
    return { firstMonthStart, secondMonthStart };
  }

  isDateSelected(
    date: Date,
    startDate: Date | null,
    endDate: Date | null
  ): boolean {
    return isSameDay(date, startDate) || isSameDay(date, endDate);
  }

  isInRange(date: Date, startDate: Date | null, endDate: Date | null): boolean {
    if (!startDate || !endDate) {
      return false;
    }

    // Check if the date is within the interval, including start and end dates
    const withinInterval = isWithinInterval(date, {
      start: startDate,
      end: endDate,
    });

    // Ensure the date is not the same as the start or end date
    const isNotStart = !isSameDay(date, startDate);
    const isNotEnd = !isSameDay(date, endDate);

    return withinInterval && isNotStart && isNotEnd;
  }

  isToday(date: Date): boolean {
    return isToday(date);
  }

  isSelectedDate(date: Date, selectedDate: Date): boolean {
    return isSameDay(date, selectedDate);
  }
}
